import React, { useEffect } from 'react';
import { getContentsDatas } from '../../../api/utils/lms/lmsApi';
import { getContentView } from '../../../api/utils/api/learningApi';
import { getContentsDatas_light } from '../../../api/utils/api/lightApi';
import {
  CONTENT_HOST,
  ContentsData,
  ContentType,
  getContentUrl,
  getTakeclassUrl,
  isPlayerHost,
  PlayerCloseEvent,
  PlayerMessageEvent,
  PopupMessageEvent,
} from '../helpers';

interface UseContentsMessageParams {
  /** 콘텐츠 iframe 참조 (player 메시지의 발신 창 검증에 사용) */
  iframeRef: React.RefObject<HTMLIFrameElement>;
  /** 조회한 콘텐츠를 저장하는 함수 */
  saveContentsData: (data: ContentsData) => void;
  /** todoView 토큰 갱신 함수 */
  setTodoAccessToken: React.Dispatch<React.SetStateAction<string>>;
  /** 항상 최신 postTokenToPlayer를 가리키는 ref */
  postTokenToPlayerRef: React.MutableRefObject<() => void>;
}

/**
 * 창간 message 이벤트를 수신해 콘텐츠 표시/토큰 전송/창 닫기를 처리하는 훅.
 * @description
 * - player 메시지(PLAYER_*)는 origin+source가 player iframe일 때만 처리한다(토큰 전송/종료).
 * - 오프너 메시지(Popup)는 origin이 동일하고 source가 window.opener일 때만 처리해 위조를 차단한다.
 * - 리스너는 마운트 시 한 번 등록되며, 최신 상태는 전달받은 안정적인 콜백/ref로 참조한다.
 */
export const useContentsMessage = ({
  iframeRef,
  saveContentsData,
  setTodoAccessToken,
  postTokenToPlayerRef,
}: UseContentsMessageParams) => {
  useEffect(() => {
    /**
     * 수업자료(contentsView) 콘텐츠를 조회해 iframe URL로 저장한다.
     * @description 조회 실패 시(또는 API가 undefined 반환 시) 빈 콘텐츠로 저장해 로딩 스피너에 갇히지 않게 한다.
     */
    const getTakeclassData = async (
      classId: string,
      contentId: string,
      isLight?: boolean,
    ) => {
      try {
        const contents = isLight
          ? await getContentsDatas_light(classId, contentId)
          : await getContentsDatas(classId, contentId);

        // contentsType : HTML, Vimeo, Jebspl(Acts수업자료)
        // Vimeo는 getTakeclassUrl이 vimeoKey(재생 URL)를 반환해 ReactPlayer로 렌더된다.
        saveContentsData({
          contentsUrl: getTakeclassUrl(contents),
          contentsType: contents.type as ContentType,
        });
      } catch (error) {
        // API 실패 시 로딩 스피너에 갇히지 않도록 에러 상태로 전환
        console.error('수업자료 콘텐츠 로드 실패:', error);
        saveContentsData({ contentsUrl: '', contentsType: '' });
      }
    };

    /**
     * 교사대학(learningView) 콘텐츠를 조회해 렌더링할 URL로 저장한다.
     * @description
     * - Vimeo면 vimeo_key(재생 URL, ReactPlayer로 렌더), 그 외에는 exe_file.resource_path 기반 HTML URL을 사용한다.
     * - exe_file/resource_path가 없거나 조회에 실패하면 빈 콘텐츠로 저장한다.
     * @see LearningLessonContentType (src/api/utils/api/learningApi.ts) — 응답 스키마
     */
    const getLearningView = async (learningId: string, lessonId: string) => {
      try {
        const data = await getContentView(learningId, lessonId);

        let url;
        switch (data.type) {
          case 'Vimeo':
            url = getContentUrl.Vimeo({
              vimeoKey: data.vimeo_key,
              type: 'Vimeo',
            });
            break;
          default:
            // exe_file/resource_path가 없으면 빈 URL로 두어 "비정상적인 접근" 분기로 유도
            url = data.exe_file?.resource_path
              ? `${CONTENT_HOST}/${data.exe_file.resource_path}`
              : '';
        }

        saveContentsData({
          contentsUrl: url,
          contentsType: data.type as ContentType,
        });
      } catch (error) {
        // API 실패 시 로딩 스피너에 갇히지 않도록 에러 상태로 전환
        console.error('교사대학 콘텐츠 로드 실패:', error);
        saveContentsData({ contentsUrl: '', contentsType: '' });
      }
    };

    const handleMessage = (
      event: MessageEvent<
        PopupMessageEvent | PlayerMessageEvent | PlayerCloseEvent
      >,
    ) => {
      const data = event.data;

      // player iframe이 보내는 메시지 (origin과 source가 player iframe인 경우에만 처리)
      if (data?.type === 'PLAYER_READY' || data?.type === 'PLAYER_CLOSE') {
        if (process.env.NODE_ENV === 'development') {
          console.log(
            `[ContentsView] player 메시지 수신: ${data.type}, origin=${event.origin}, isPlayerHost=${isPlayerHost(event.origin)}, isIframe=${event.source === iframeRef.current?.contentWindow}`,
          );
        }
        const isFromPlayer =
          isPlayerHost(event.origin) &&
          event.source === iframeRef.current?.contentWindow;
        if (!isFromPlayer) return;

        if (data.type === 'PLAYER_READY') {
          // 준비 완료 신호 -> 토큰 전송 (리스너 등록 전 전송 레이스 방지)
          postTokenToPlayerRef.current();
        } else {
          // 'PLAYER_CLOSE' 닫기 요청
          if (data.confirm) {
            // confirm==true일 경우 종료 다이얼로그 표시
            if (window.confirm('정말 종료하시겠습니까?')) {
              window.close();
            }
          } else {
            // confirm==false일 경우 바로 닫기
            window.close();
          }
        }
      }

      // todoView/contentsView/learningView/closeEvent 처리 전 event.source가 window.opener와 일치하는지 확인하여 동일 오리진 내 위조 메시지 차단
      if (
        event.origin !== window.location.origin ||
        event.source !== window.opener
      ) {
        return;
      }

      // data.type으로 분기하여 판별 유니온 contents가 타입별로 좁혀지게 한다
      switch (data.type) {
        case 'contentsView':
          getTakeclassData(
            data.contents.classId!,
            data.contents.contentId,
            data.contents.isLight,
          );
          break;
        // 학생할일 (젭스온, 젭스플 상관 없이)
        case 'todoView':
          saveContentsData({
            contentsUrl: data.contents.url,
            contentsType: 'todoView',
          });
          // 오프너가 토큰을 안 보냈을 때 localStorage에서 읽은 초기값을 덮어쓰지 않음
          if (data.contents.accessToken) {
            setTodoAccessToken(data.contents.accessToken);
          }
          break;
        //교사대학
        case 'learningView':
          getLearningView(data.contents.learningId, data.contents.lessonId);
          break;
        case 'closeEvent':
          if (window.opener && !window.opener.closed) {
            const parentUrl = window.opener.location.href;
            if (parentUrl.match(/student\/(class|homework)\/[^/]+/)) {
              window.opener.location.reload();
            }
          }
          // 창 닫기
          window.close();
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [saveContentsData, setTodoAccessToken, postTokenToPlayerRef, iframeRef]);
};
